import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { useFriends } from '@/hooks/useFriends';

// Export the return type from useFriends
type FriendsContextValue = ReturnType<typeof useFriends>;

const FriendsContext = createContext<FriendsContextValue | null>(null);

interface FriendsProviderProps {
  children: ReactNode;
}

/**
 * FriendsProvider - Provides centralized friends data to all components
 *
 * CRITICAL: This provider ensures useFriends() is called ONLY ONCE at the top level,
 * preventing multiple event listener registrations and duplicate friend:list responses.
 *
 * All child components should use useFriendsContext() instead of calling useFriends() directly.
 */
export function FriendsProvider({ children }: FriendsProviderProps) {
  // Call useFriends ONCE at this level
  const friendsData = useFriends();

  return (
    <FriendsContext.Provider value={friendsData}>
      {children}
    </FriendsContext.Provider>
  );
}

/**
 * useFriendsContext - Hook to access friends data from context
 *
 * Usage:
 * const { friends, friendRequests, sendFriendRequest, ... } = useFriendsContext();
 *
 * This replaces direct calls to useFriends() in components to prevent
 * multiple socket event registrations.
 */
export function useFriendsContext(): FriendsContextValue {
  const context = useContext(FriendsContext);
  if (!context) {
    throw new Error('useFriendsContext must be used within FriendsProvider');
  }
  return context;
}
